import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  BarController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { formatNum } from "../DataTable";
import { useSelector } from "react-redux";

ChartJS.register(
  BarController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const DONUT_COLORS = [
  "#5CC7C1",
  "#B2B3F5",
  "#52ABE1",
  "#5C74C7",
  "#F06547",
  "#3CC3DF",
  "#434549",
  "#FB83B3",
  "#6FD195",
  "#F4CF3B",
];
const CARD_IDS = ["compareBar", "topDonut"];

function getLocaleBySettings(priceFormat = {}) {
  if (priceFormat?.currency === "INR") return "en-IN";
  if (priceFormat?.thousand_separator === ".") return "nl-NL";
  return "en-US";
}

function getScaleConfig(priceFormat = {}) {
  const showAmountIn = String(priceFormat?.show_amount_in || "Thousand").toLowerCase();
  if (showAmountIn.includes("billion")) return { divisor: 1000000000, unitLabel: "Billion" };
  if (showAmountIn.includes("million")) return { divisor: 1000000, unitLabel: "Million" };
  if (showAmountIn.includes("lakh")) return { divisor: 100000, unitLabel: "Lakh" };
  if (showAmountIn.includes("crore")) return { divisor: 10000000, unitLabel: "Crore" };
  return { divisor: 1000, unitLabel: "Thousand" };
}

function formatAxisNumber(n, priceFormat = {}) {
  const locale = getLocaleBySettings(priceFormat);
  const decimal = Number(priceFormat?.decimal_point || 2);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimal,
  }).format(n);
}

function formatCompact(v) {
  const n = Number(v) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(Math.round(n));
}

function TopNDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const items = [10, 20, 30].map((n) => ({ key: String(n), label: String(n) }));
  const selected = items.find((i) => i.key === value) || items[0];
  return (
    <div style={styles.dropdownWrap} ref={ref}>
      <button style={{ ...styles.control, width: 'auto' }} onClick={() => setOpen((v) => !v)} type="button">
        Top {selected.label} <span style={styles.chevron}>▾</span>
      </button>
      {open && (
        <div style={styles.menu}>
          {items.map((item) => (
            <button
              key={item.key}
              style={{ ...styles.option, background: item.key === value ? "#eef2f3" : "#fff" }}
              onClick={() => {
                onChange(item.key);
                setOpen(false);
              }}
              type="button"
            >
              Top {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ByTypeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const items = [
    { key: "Amount", label: "By Amount" },
    { key: "Qty", label: "By Qty" },
  ];
  const selected = items.find((i) => i.key === value) || items[0];
  return (
    <div style={styles.dropdownWrap} ref={ref}>
      <button style={{ ...styles.control, width: 106 }} onClick={() => setOpen((v) => !v)} type="button">
        {selected.label} <span style={styles.chevron}>▾</span>
      </button>
      {open && (
        <div style={styles.menu}>
          {items.map((item) => (
            <button
              key={item.key}
              style={{ ...styles.option, background: item.key === value ? "#eef2f3" : "#fff" }}
              onClick={() => {
                onChange(item.key);
                setOpen(false);
              }}
              type="button"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ThreeDotsMenu({ chartRef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handlePrint = () => {
    window.print();
    setOpen(false);
  };

  const handleDownload = () => {
    const chart = chartRef?.current;
    if (!chart) return;
    const url = chart.toBase64Image();
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart.png";
    link.click();
    setOpen(false);
  };

  return (
    <div style={styles.menuWrapper} ref={ref}>
      <div style={styles.dots} onClick={() => setOpen((v) => !v)}>
        ⋮
      </div>
      {open && (
        <div style={styles.chartMenu}>
          <div style={styles.menuIconRow}>
            <div style={styles.menuIconBtn} onClick={handlePrint} title="Print">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </div>
            <div style={styles.menuIconBtn} onClick={handleDownload} title="Download">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <path d="M19 16 v3 M17.5 19 h3" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TopDonutCard({ rows, onDragStart, onDragEnd }) {
  const chartRef = useRef();
  const [topN, setTopN] = useState("10");
  const [byType, setByType] = useState("Amount");

  const sliced = useMemo(() => {
    const n = parseInt(topN, 10) || 10;
    const list = [...rows].sort((a, b) => {
      if (byType === "Qty") return (Number(b.qty) || 0) - (Number(a.qty) || 0);
      return (Number(b.sales) || 0) - (Number(a.sales) || 0);
    });
    return list.slice(0, n);
  }, [rows, topN, byType]);

  const values = sliced.map((r) => (byType === "Qty" ? Number(r.qty) || 0 : Number(r.sales) || 0));
  const total = values.reduce((s, v) => s + v, 0);

  const data = {
    labels: sliced.map((r) => r.sku || "SKU"),
    datasets: [
      {
        data: values,
        backgroundColor: sliced.map((_, i) => DONUT_COLORS[i % DONUT_COLORS.length]),
        borderWidth: 0,
        cutout: "72%",
      },
    ],
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          title="Drag to reorder chart"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>Top Selling SKUs</p>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByTypeDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <Doughnut
          ref={chartRef}
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  usePointStyle: true,
                  pointStyle: "circle",
                  boxWidth: 8,
                  boxHeight: 8,
                  color: "rgba(0, 0, 0, 0.7)",
                  padding: 8,
                  font: { family: "Inter, Segoe UI, sans-serif", size: 10, weight: "400" },
                },
              },
              tooltip: {
                callbacks: {
                  label(ctx) {
                    const v = Number(ctx.raw) || 0;
                    const pct = total > 0 ? ((v / total) * 100).toFixed(1) : "0";
                    return ` ${ctx.label}: ${formatNum(v)} (${pct}%)`;
                  },
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
}

function CompareBarCard({ rows, priceFormat = {}, onDragStart, onDragEnd }) {
  const chartRef = useRef();
  const [topN, setTopN] = useState("10");
  const [byType, setByType] = useState("Amount");
  const currency = priceFormat?.currency || "USD";
  const { divisor, unitLabel } = getScaleConfig(priceFormat);
  const displayRows = useMemo(() => {
    const list = [...rows].sort((a, b) => (Number(b.sales) || 0) - (Number(a.sales) || 0));
    return list.slice(0, parseInt(topN, 10) || 10);
  }, [rows, topN]);

  const labels = displayRows.map((r) => r.sku || "SKU");
  const tagData = displayRows.map((r) => Number(r.tag_price) || 0);
  const sellingData = displayRows.map((r) => Number(r.sales) || 0);
  const qtyData = displayRows.map((r) => Number(r.qty) || 0);
  const discountData = displayRows.map((r) =>
    Math.max((Number(r.tag_price) || 0) - (Number(r.sales) || 0), 0)
  );
  const yMax =
    byType === "Qty"
      ? Math.max(...qtyData, 0)
      : Math.max(...tagData, ...sellingData, ...discountData, 0);

  const data = {
    labels,
    datasets:
      byType === "Qty"
        ? [
            {
              label: "Qty",
              data: qtyData,
              backgroundColor: "#5C74C7",
              barThickness: 20,
              categoryPercentage: 0.6,
              barPercentage: 0.9,
            },
          ]
        : [
            {
              label: "Tag Price",
              data: tagData,
              backgroundColor: "#5CC7C1",
              barThickness: 20,
              categoryPercentage: 0.6,
              barPercentage: 0.9,
            },
            {
              label: "Selling Price",
              data: sellingData,
              backgroundColor: "#5C74C7",
              barThickness: 20,
              categoryPercentage: 0.6,
              barPercentage: 0.9,
            },
            {
              label: "Discount",
              data: discountData,
              backgroundColor: "#52ABE1",
              barThickness: 20,
              categoryPercentage: 0.6,
              barPercentage: 0.9,
            },
          ],
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          title="Drag to reorder chart"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>Comparing sales, tag prices, and discounts</p>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByTypeDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <Bar
          ref={chartRef}
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  usePointStyle: true,
                  pointStyle: "rectRounded",
                  boxWidth: 8,
                  boxHeight: 8,
                  color: "rgba(0, 0, 0, 0.7)",
                  padding: 10,
                  font: { family: "Segoe UI", size: 11, weight: "400" },
                },
              },
              tooltip: {
                callbacks: {
                  label(ctx) {
                    const v = Number(ctx.raw) || 0;
                    return ` ${ctx.dataset.label}: ${formatCompact(v)}`;
                  },
                },
              },
            },
            scales: {
              x: {
                grid: {
                  display: false,
                  drawBorder: false,
                },
                ticks: { color: "#6b7280", font: { size: 11 }, maxRotation: 0, minRotation: 0 },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  color: "#6b7280",
                  callback: (v) =>
                    formatAxisNumber((Number(v) || 0) / divisor, priceFormat),
                },
                suggestedMax: yMax,
                grid: { color: "rgba(0,0,0,0.08)", drawBorder: false },
                title: {
                  display: true,
                  text: byType === "Qty" ? "Qty" : `${currency} (${unitLabel})`,
                  color: "#6b7280",
                  font: { size: 11 },
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
}

export default function SKUCharts({ rows = [] }) {
  const safeRows = useMemo(() => rows.filter(Boolean), [rows]);
  const dragSourceRef = useRef(null);
  const [order, setOrder] = useState(CARD_IDS);
  const [dragging, setDragging] = useState(null);
  const priceFormat = useSelector(
    (state) => state?.app?.organisation_settings?.price_format || {},
  );

  const startDrag = (id, e) => {
    dragSourceRef.current = id;
    setDragging(id);
    if (e?.dataTransfer) {
      e.dataTransfer.setData("text/plain", id);
      e.dataTransfer.effectAllowed = "move";
    }
  };

  const endDrag = () => {
    dragSourceRef.current = null;
    setDragging(null);
  };

  const handleDrop = (targetId, e) => {
    e.preventDefault();
    const sourceId =
      dragSourceRef.current || e.dataTransfer?.getData("text/plain") || dragging;
    if (!sourceId || sourceId === targetId) {
      endDrag();
      return;
    }
    setOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(sourceId);
      const to = next.indexOf(targetId);
      if (from < 0 || to < 0) return prev;
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    endDrag();
  };

  const cards = {
    compareBar: (
      <CompareBarCard
        rows={safeRows}
        priceFormat={priceFormat}
        onDragStart={(e) => startDrag("compareBar", e)}
        onDragEnd={endDrag}
      />
    ),
    topDonut: (
      <TopDonutCard
        rows={safeRows}
        onDragStart={(e) => startDrag("topDonut", e)}
        onDragEnd={endDrag}
      />
    ),
  };

  return (
    <div style={styles.root}>
      <div style={styles.grid}>
        {order.map((id) => (
          <div
            key={id}
            style={styles.col}
            onDragOver={(e) => {
              e.preventDefault();
              if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
            }}
            onDrop={(e) => handleDrop(id, e)}
          >
            {cards[id]}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  root: { padding: "0 20px 16px", background: "#fff" },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 },
  col: { minWidth: 0 },
  card: {
    boxSizing: "border-box",
    background: "#FFFFFF",
    border: "1px solid #E3E3E3",
    borderRadius: 8,
    padding: "12px 16px 8px",
    height: "100%",
  },
  cardHeader: { display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 8, marginBottom: 8 },
  headerRight: { display: "flex", alignItems: "center", gap: 5, flexShrink: 0 },
  title: { margin: 0, color: "#606060", fontFamily: "Segoe UI", fontSize: 16, fontWeight: 600 },
  sectionTitle: {
    margin: "10px 2px",
    color: "#606060",
    fontFamily: "Segoe UI",
    fontSize: 16,
    fontWeight: 600,
    flex: 1,
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  chartWrap: { height: 430 },
  dragHandle: {
    cursor: "grab",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "2px 3px",
    lineHeight: 0,
    flexShrink: 0,
  },
  dots: {
    cursor: "pointer",
    fontSize: 22,
    color: "#606060",
    padding: "0 4px",
    lineHeight: 1,
    userSelect: "none",
  },
  menuWrapper: { position: "relative" },
  chartMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 6px)",
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    zIndex: 30,
    padding: "10px 14px",
  },
  menuIconRow: {
    display: "flex",
    gap: 16,
    alignItems: "center",
  },
  menuIconBtn: {
    cursor: "pointer",
    padding: 6,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dropdownWrap: { position: "relative" },
  control: {
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    border: "1px solid #D9D9D9",
    borderRadius: 4,
    background: "#fff",
    color: "#2A2A2A",
    fontFamily: "Segoe UI",
    fontSize: 14,
    cursor: "pointer",
  },
  chevron: { fontSize: 14, color: "#606060" },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 6,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    overflow: "hidden",
    zIndex: 20,
    minWidth: 110,
  },
  option: {
    width: "100%",
    border: "none",
    background: "#fff",
    color: "#606060",
    padding: "8px 10px",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "Segoe UI",
    fontSize: 13,
  },
};
